import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'

/** Altyapıyı görüntüleme (müşteri veri kapsamında) ve düzenleme yetkileri; düzenleyen görüntüleyebilir. */
export const INFRA_READ_PERM = ['infra.servers.read', 'infra.servers.write']
export const INFRA_WRITE_PERM = 'infra.servers.write'

export type FootprintModule = { code: string; name: string; validUntil: string | null; placementIds: string[] }
export type FootprintPlacement = {
  id: string
  serverName: string
  hostname: string
  environment: string
  url: string
  status: string
  moduleCodes: string[]
  domains: string[]
}
export type Footprint = { modules: FootprintModule[]; placements: FootprintPlacement[]; unplacedDomains: string[] }

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))
const list = (v: unknown) => (Array.isArray(v) ? v.map(String) : [])

const key = (accountId: string) => ['crm', 'footprint', accountId] as const

/** Müşteride neyin nerede çalıştığı: lisanslı modüller, sunucu yerleşimleri ve domainler. */
export const useFootprint = (accountId: string, enabled: boolean) =>
  useQuery({
    queryKey: key(accountId),
    enabled: enabled && !!accountId,
    queryFn: async () => {
      const row = await api.get<Row>(`/api/v1/infra/accounts/${accountId}/footprint`)
      return {
        modules: (Array.isArray(row.modules) ? (row.modules as Row[]) : []).map((m) => ({
          code: s(m.code),
          name: s(m.name) || s(m.code),
          validUntil: m.valid_until ? s(m.valid_until) : null,
          placementIds: list(m.placement_ids),
        })),
        placements: (Array.isArray(row.placements) ? (row.placements as Row[]) : []).map((p) => ({
          id: s(p.id),
          serverName: s(p.server_name),
          hostname: s(p.hostname),
          environment: s(p.environment),
          url: s(p.url_summary),
          status: s(p.status),
          moduleCodes: list(p.module_codes),
          domains: list(p.domains),
        })),
        unplacedDomains: list(row.unplaced_domains),
      } satisfies Footprint
    },
  })

export function useSetPlacementModules(accountId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ placementId, codes }: { placementId: string; codes: string[] }) => api.put(`/api/v1/infra/placements/${placementId}/modules`, { module_codes: codes }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(accountId) }),
  })
}
